import { HashMap, TranslocoTranspiler, TranspileParams, isString } from '@jsverse/transloco';
import IntlMessageFormat from 'intl-messageformat';

export class IntlMessageFormatTranspiler implements TranslocoTranspiler {
  private readonly _messageCache = new Map<string, IntlMessageFormat>();

  transpile(params: TranspileParams<unknown>): string {
    const value = params.value;

    if (!isString(value)) {
      return value as string;
    }

    if (!this._hasPlaceholders(value)) {
      return value;
    }

    try {
      let processedValue = value;

      // Check if this uses ICU rich text formatting (has corresponding function parameters)
      const hasRichTextFormatting = this._hasRichTextFormatting(value, params.params as HashMap);

      if (!hasRichTextFormatting) {
        // Escape HTML tags for literal HTML
        processedValue = this._escapeHtml(value);
      }

      const message = this._getOrCreateMessage(processedValue, params.key);
      const formatted = message.format(params.params as HashMap);

      // Unescape if we escaped earlier
      const result = hasRichTextFormatting ? formatted : this._unescapeHtml(formatted);

      return result ?? value;
    } catch (error) {
      console.error('Error formatting message:', error);
      return value;
    }
  }

  private _hasPlaceholders(value: string): boolean {
    return value.includes('{') && value.includes('}');
  }

  /**
   * Determines if the message uses rich text formatting by checking if HTML-like tags
   * in the message have corresponding function parameters.
   *
   * We extract tag names by removing the < and > brackets and check if matching
   * function parameters exist. This allows us to distinguish between rich text
   * formatting and literal HTML that should be escaped.
   *
   * The reason to do this is to support our current implementation
   */
  private _hasRichTextFormatting(value: string, params: HashMap): boolean {
    // Extract potential rich text tag names from the message
    const richTextTags = value.match(/<(\w+)>/g);
    if (!richTextTags) {
      return false;
    }

    // Check if any of these tag names exist as function parameters
    return richTextTags.some((tag) => {
      const tagName = tag.slice(1, -1); // Remove < and >
      return typeof params[tagName] === 'function';
    });
  }

  private _getCacheKey(value: string, key?: string): string {
    return key ? `${key}:${value}` : value;
  }

  private _getOrCreateMessage(value: string, key?: string): IntlMessageFormat {
    const cacheKey = this._getCacheKey(value, key);

    let message = this._messageCache.get(cacheKey);
    if (!message) {
      message = new IntlMessageFormat(value);
      this._messageCache.set(cacheKey, message);
    }

    return message;
  }

  private _escapeHtml(value: string): string {
    return value.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private _unescapeHtml(value: string): string {
    return value.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  }
}
